import React, { useEffect, useRef, useState } from "react";

interface SelectionActionsTooltipProps {
  isDarkMode: boolean;
  selectedModel?: string;
  selectedAgentName?: string | null;
}

interface Position {
  top: number;
  left: number;
}

const getDisplayName = (model?: string, agentName?: string | null): string => {
  if (agentName && agentName.trim()) return agentName.trim();
  const map: Record<string, string> = {
    'gpt': 'ChatGPT',
    'chat gpt': 'ChatGPT',
    'chatgpt': 'ChatGPT',
    'claude': 'Claude',
    'gemini': 'Gemini',
    'llama': 'LLaMA',
    'deepseek': 'DeepSeek',
    'qwen': 'Qwen',
    'glm': 'GLM',
    'sambanova': 'Sambanova',
    'cerebras': 'Cerebras',
    'upstage': 'Solar Pro',
    'web-search': 'Web Search',
    'web search': 'Web Search',
    'deep-research': 'Deep Research',
    'lara': 'LARA',
  };
  const key = (model || '').toLowerCase();
  return map[key] || model || 'AI';
};

export const SelectionActionsTooltip: React.FC<SelectionActionsTooltipProps> = ({ isDarkMode, selectedModel, selectedAgentName }) => {
  const [visible, setVisible] = useState(false);
  const [text, setText] = useState("");
  const [pos, setPos] = useState<Position>({ top: 0, left: 0 });
  const hideTimeoutRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastRangeRef = useRef<Range | null>(null);
  const isPointerDownRef = useRef(false);
  const [copied, setCopied] = useState(false);

  // Safely read current tooltip width or return a reasonable fallback
  const getTooltipWidth = (): number => {
    const fallback = 240; // px
    try {
      const el = containerRef.current;
      if (!el) return fallback;
      // Include borders/padding via offsetWidth
      return Math.max(el.offsetWidth || 0, fallback);
    } catch {
      return fallback;
    }
  };

  // Clamp the horizontal position so the tooltip remains within viewport
  const clampHorizontal = (centerX: number, width?: number): number => {
    const padding = 8; // viewport padding
    const w = width ?? getTooltipWidth();
    const minLeft = padding + w / 2;
    const maxLeft = Math.max(minLeft, (window.innerWidth || document.documentElement.clientWidth) - padding - w / 2);
    return Math.min(Math.max(centerX, minLeft), maxLeft);
  };

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const isValidSelectionTarget = (node: Node | null): boolean => {
    if (!node) return false;
    if (node instanceof HTMLElement) {
      // Do not show for inputs, textareas, contenteditable
      const tag = node.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return false;
      if (node.isContentEditable) return false;
      // Avoid buttons/interactive controls
      if (["button", "select", "option", "label", "svg"].includes(tag)) return false;
    }
    return true;
  };

  const nodeIsInsideAI = (node: Node | null): boolean => {
    let el: HTMLElement | null = (node as HTMLElement) instanceof HTMLElement ? (node as HTMLElement) : (node?.parentElement || null);
    while (el) {
      if (el.getAttribute && el.getAttribute('data-message-type') === 'ai') return true;
      el = el.parentElement;
    }
    return false;
  };

  const updateFromSelection = () => {
    try {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        setVisible(false);
        setText("");
        return;
      }

      const selectedText = (sel.toString() || "").trim();
      if (!selectedText) {
        setVisible(false);
        setText("");
        return;
      }

      // Validate target
      const anchorNode = sel.anchorNode;
      const focusNode = sel.focusNode;
      // Only show when selection is within AI message containers
      if (!nodeIsInsideAI(anchorNode as Node) || !nodeIsInsideAI(focusNode as Node)) {
        setVisible(false);
        return;
      }

      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (!rect || (rect.top === 0 && rect.bottom === 0 && rect.left === 0 && rect.right === 0)) {
        setVisible(false);
        return;
      }

      const margin = 8; // px offset above selection
      // For fixed positioning, use viewport coordinates without scrollY
      const top = Math.max(8, rect.top - margin);
      const centerX = rect.left + rect.width / 2;

      // Tentatively set position centered; after mount we will re-clamp with actual width
      setText(selectedText);
      setPos({ top, left: clampHorizontal(centerX) });
      setVisible(true);
      // Save range to restore after scroll if the browser collapses selection
      lastRangeRef.current = range.cloneRange();
    } catch (e) {
      setVisible(false);
    }
  };

  useEffect(() => {
    const onSelectionChange = () => updateFromSelection();
    const onMouseUp = () => updateFromSelection();
    const onKeyUp = () => updateFromSelection();
    const onScroll = () => {
      // Maintain selection on scroll, but keep tooltip position fixed
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const sel = window.getSelection();
        if (visible && lastRangeRef.current && sel && (sel.isCollapsed || sel.rangeCount === 0) && !isPointerDownRef.current) {
          try {
            sel.removeAllRanges();
            sel.addRange(lastRangeRef.current);
          } catch {}
        }
      });
    };
    const onResize = () => {
      // Recompute clamped horizontal position on viewport resize
      if (!visible || !lastRangeRef.current) return;
      try {
        const rect = lastRangeRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        setPos((prev) => ({ top: prev.top, left: clampHorizontal(centerX) }));
      } catch {}
    };
    const onMouseDown = (e: MouseEvent) => {
      // If clicking outside tooltip, clear selection and hide
      const target = e.target as Node;
      if (containerRef.current && containerRef.current.contains(target as Node)) return;
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed && visible) {
        sel.removeAllRanges();
        setVisible(false);
      }
    };
    const onPointerDown = () => { isPointerDownRef.current = true; };
    const onPointerUp = () => { isPointerDownRef.current = false; };

    document.addEventListener('selectionchange', onSelectionChange);
    window.addEventListener('mouseup', onMouseUp, { passive: true });
    window.addEventListener('keyup', onKeyUp, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    document.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });

    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
      window.removeEventListener('mouseup', onMouseUp as any);
      window.removeEventListener('keyup', onKeyUp as any);
      window.removeEventListener('scroll', onScroll as any);
      window.removeEventListener('resize', onResize as any);
      document.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('pointerdown', onPointerDown as any);
      window.removeEventListener('pointerup', onPointerUp as any);
      clearHideTimeout();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [visible]);

  // After the tooltip becomes visible, measure the actual width and re-clamp horizontally
  useEffect(() => {
    if (!visible) return;
    // Next frame to ensure DOM is painted
    const id = requestAnimationFrame(() => {
      if (!lastRangeRef.current) return;
      try {
        const rect = lastRangeRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const width = getTooltipWidth();
        setPos((prev) => ({ top: prev.top, left: clampHorizontal(centerX, width) }));
      } catch {}
    });
    return () => cancelAnimationFrame(id);
  }, [visible]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      // Show copied state briefly with a tick animation, then hide
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
        setVisible(false);
      }, 900);
    } catch (e) {
      // Fallback: create temp textarea
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
        setVisible(false);
      }, 900);
    }
  };

  const handleAskAI = () => {
    // Dispatch events to prefill and focus chat input
    window.dispatchEvent(new CustomEvent('prefillChatInput', { detail: { text } }));
    window.dispatchEvent(new Event('focusChatInput'));
    setVisible(false);
  };

  if (!visible) return null;

  // Center horizontally around left position, adjust later with transform
  const style: React.CSSProperties = {
    position: 'fixed',
    top: pos.top,
    left: pos.left,
    transform: 'translate(-50%, -100%)',
    zIndex: 9999,
    pointerEvents: 'auto',
  };

  return (
    <div style={style} className="select-none" ref={containerRef}>
      <div
        className={`flex items-center gap-2 p-1.5 rounded-2xl shadow-2xl border backdrop-blur-sm ${
          isDarkMode
            ? 'bg-[#181818]/98 border-black/20 text-gray-100'
            : 'bg-white/98 border-gray-200 text-gray-900'
        }`}
      >
        <button
          onClick={handleAskAI}
          className={`flex w-[max-content] items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-xl transition-colors border ${
            isDarkMode
              ? 'bg-[#202020] border-black/30 text-gray-100 hover:bg-[#262626]'
              : 'bg-[#f3f4f6] border-gray-200 text-gray-900 hover:bg-[#e7e9ee]'
          }`}
        >
          <span>{`Ask ${getDisplayName(selectedModel, selectedAgentName || undefined)}`}</span>
        </button>
        <button
          onClick={handleCopy}
          className={`px-3 py-1.5 text-sm rounded-xl transition-colors border relative overflow-hidden ${
            isDarkMode
              ? 'bg-[#202020] border-black/30 text-gray-100 hover:bg-[#262626]'
              : 'bg-[#f3f4f6] border-gray-200 text-gray-900 hover:bg-[#e7e9ee]'
          }`}
          disabled={copied}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'transform 150ms ease, opacity 150ms ease',
              transform: copied ? 'translateY(-8px) scale(0.95)' : 'translateY(0) scale(1)',
              opacity: copied ? 0 : 1,
            }}
          >
            Copy
          </span>
          <span
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: copied ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -20%) scale(0.6)',
              transition: 'transform 200ms ease, opacity 150ms ease',
              opacity: copied ? 1 : 0,
              pointerEvents: 'none',
              color: isDarkMode ? '#22c55e' : '#16a34a',
              fontWeight: 700,
            }}
            aria-hidden
          >
            ✓
          </span>
        </button>
      </div>
    </div>
  );
};
export default SelectionActionsTooltip;
